import {announce} from '@github-ui/aria-live'
import {currentIssueStatus, type IssueStatusOption, needsDuplicateIssue} from '@github-ui/issue-actions/issue-status'
import {reportIssueStatusesUsage} from '@github-ui/issue-actions/issue-statuses-analytics'
import {duplicateFromPickerItem, type SetIssueStatus} from '@github-ui/issue-actions/SetIssueStatusMutation'
import {useIssueState} from '@github-ui/issue-pr-state'
import type {IssueStatusColor} from '@github-ui/issue-pr-state/issue-status-visuals'
import {IssueStatusIcon, issueStatusVisual} from '@github-ui/issue-pr-state/IssueStatusIcon'
import type {IssuePickerItem} from '@github-ui/item-picker/IssuePicker'
import {RepositoryAndIssuePicker} from '@github-ui/item-picker/RepositoryAndIssuePicker'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, StateLabel} from '@primer/react'
import type React from 'react'
import {useCallback, useRef, useState} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import {DuplicateIssueLink, HeaderState, type HeaderStateFragment} from './HeaderState'
import styles from './HeaderState.module.css'
import {useIssueHeaderDataContext} from './IssueHeaderDataContext'

export type HeaderStatusMenuProps = {
  isSticky: boolean
  stateData: HeaderStateFragment
  status: IssueStatusOption | null
  statuses: readonly IssueStatusOption[]
  issueId: string
  repositoryNameWithOwner?: string
  owner: string
  repo: string
  setIssueStatus: SetIssueStatus
  viewerCanSetStatus: boolean
  onIssueUpdate?: () => void
}

/** Reads the issue from the header context so HeaderMetadata doesn't have to widen its fragment. */
export function HeaderStatus({
  isSticky,
  setIssueStatus,
  stateData,
  onIssueUpdate,
}: {
  isSticky: boolean
  setIssueStatus: SetIssueStatus
  stateData: HeaderStateFragment
  onIssueUpdate: () => void
}) {
  const issue = useIssueHeaderDataContext()
  return (
    <HeaderStatusMenu
      isSticky={isSticky}
      stateData={stateData}
      status={issue.status}
      statuses={issue.repository.owner.planningStatuses?.nodes?.filter(isStatus) ?? []}
      issueId={issue.id}
      repositoryNameWithOwner={issue.repository.nameWithOwner}
      owner={issue.repository.owner.login}
      repo={issue.repository.name}
      setIssueStatus={setIssueStatus}
      viewerCanSetStatus={issue.viewerCanSetStatus}
      onIssueUpdate={onIssueUpdate}
    />
  )
}

const STATUS_LABEL_STYLES: Record<IssueStatusColor, React.CSSProperties> = {
  open: {
    backgroundColor: 'var(--bgColor-open-emphasis)',
    boxShadow: 'var(--boxShadow-thin) var(--borderColor-open-emphasis)',
  },
  done: {
    backgroundColor: 'var(--bgColor-done-emphasis)',
    boxShadow: 'var(--boxShadow-thin) var(--borderColor-done-emphasis)',
  },
  muted: {
    backgroundColor: 'var(--bgColor-neutral-emphasis)',
    boxShadow: 'var(--boxShadow-thin) var(--borderColor-neutral-emphasis)',
  },
}

function isStatus(status: IssueStatusOption | null): status is IssueStatusOption {
  return status !== null
}

/** The label on its own. The menu wraps it in a button and passes the caret as `children`. */
function StatusStateLabel({
  status,
  stateData,
  children,
}: {
  status: IssueStatusOption
  stateData: HeaderStateFragment
  children?: React.ReactNode
}) {
  const {icon: StatusIcon, color} = issueStatusVisual(status)
  // `StateLabel` picks its own octicon from the lifecycle state, and `open` and `closed` are the two
  // statuses it renders without one, leaving the granular icon to stand on its own.
  const iconlessStatus = stateData.state === 'CLOSED' ? 'closed' : 'open'
  return (
    <StateLabel
      data-testid={TEST_IDS.headerState}
      status={iconlessStatus}
      className={styles.stateLabel}
      style={STATUS_LABEL_STYLES[color]}
    >
      <StatusIcon className={styles.statusLabelIcon} />
      <span>{status.name}</span>
      {children}
    </StateLabel>
  )
}

export function HeaderStatusMenu({
  isSticky,
  stateData,
  status: savedStatus,
  statuses,
  issueId,
  repositoryNameWithOwner,
  owner,
  repo,
  setIssueStatus,
  viewerCanSetStatus,
  onIssueUpdate,
}: HeaderStatusMenuProps) {
  const {addToast} = useToastContext()
  const {stateString, stateStatus} = useIssueState({
    state: stateData.state,
    stateReason: stateData.stateReason,
    options: {longText: !isSticky},
  })
  const [selected, setSelected] = useState<IssueStatusOption | null>(null)
  // Drop the local pick once the saved status changes, whether it's confirmed or someone else set one.
  const [savedStatusId, setSavedStatusId] = useState(savedStatus?.id)
  if (savedStatus?.id !== savedStatusId) {
    setSavedStatusId(savedStatus?.id)
    setSelected(null)
  }
  const [open, setOpen] = useState(false)
  const [pickerType, setPickerType] = useState<'Issue' | 'Repository' | null>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const status = currentIssueStatus(stateData, statuses, selected, savedStatus)
  const statusLabel = status?.name ?? stateString

  const submit = useCallback(
    (option: IssueStatusOption, duplicateIssue?: IssuePickerItem) => {
      const usage = {
        surface: isSticky ? 'issue_header_sticky' : 'issue_header',
        cause: 'explicit_selection',
        action: 'select',
        requestComparison: savedStatus ? (savedStatus.id === option.id ? 'same' : 'different') : 'unknown',
      } as const
      reportIssueStatusesUsage({...usage, phase: 'submitted'})
      setSelected(option)
      setIssueStatus(
        {
          status: option,
          currentState: stateData.state === 'CLOSED' ? 'CLOSED' : 'OPEN',
          currentStateReason: stateData.stateReason ?? null,
          duplicateIssue: duplicateIssue && duplicateFromPickerItem(duplicateIssue),
        },
        {
          onCompleted: () => {
            reportIssueStatusesUsage({...usage, phase: 'settled', status: 'success'})
            announce(`Status set to ${option.name}`)
            onIssueUpdate?.()
          },
          onError: () => {
            reportIssueStatusesUsage({...usage, phase: 'settled', status: 'error'})
            setSelected(current => (current?.id === option.id ? null : current))
            announce(`Could not set the status to ${option.name}`, {assertive: true})
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({type: 'error', message: `Could not set the status to ${option.name}`})
          },
        },
      )
    },
    [addToast, isSticky, onIssueUpdate, savedStatus, setIssueStatus, stateData.state, stateData.stateReason],
  )

  const [duplicateStatus, setDuplicateStatus] = useState<IssueStatusOption | null>(null)
  const onSelect = (option: IssueStatusOption) => {
    if (needsDuplicateIssue(option)) {
      setDuplicateStatus(option)
      setPickerType('Issue')
      return
    }
    if (option.id !== status?.id) submit(option)
  }

  // Rendered beside the label in both paths so a duplicate always links its canonical issue.
  const duplicateLink = status && needsDuplicateIssue(status) && (
    <DuplicateIssueLink
      duplicateOf={stateData.duplicateOf}
      repositoryNameWithOwner={repositoryNameWithOwner}
      insideStateLabel={false}
    />
  )

  // A viewer who can't close or reopen the issue still sees the status it's in, just not the menu.
  // No statuses means they're off for this owner.
  if (!viewerCanSetStatus || statuses.length === 0) {
    if (!status) {
      return <HeaderState isSticky={isSticky} stateData={stateData} repositoryNameWithOwner={repositoryNameWithOwner} />
    }
    return (
      <div className={styles.statusContainer}>
        <StatusStateLabel status={status} stateData={stateData} />
        {duplicateLink}
      </div>
    )
  }

  return (
    <div className={styles.statusContainer}>
      <ActionMenu anchorRef={anchorRef} open={open} onOpenChange={setOpen}>
        <ActionMenu.Anchor>
          <button type="button" className={styles.statusButton} aria-label={`Status: ${statusLabel}`}>
            {status ? (
              <StatusStateLabel status={status} stateData={stateData}>
                <TriangleDownIcon className={styles.statusCaret} />
              </StatusStateLabel>
            ) : (
              <StateLabel data-testid={TEST_IDS.headerState} status={stateStatus} className={styles.stateLabel}>
                <span>{stateString}</span>
                <TriangleDownIcon className={styles.statusCaret} />
              </StateLabel>
            )}
          </button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="small">
          <ActionList selectionVariant="single" showDividers={false}>
            {statuses.map(option => (
              <ActionList.Item key={option.id} selected={option.id === status?.id} onSelect={() => onSelect(option)}>
                <ActionList.LeadingVisual>
                  <IssueStatusIcon status={option} />
                </ActionList.LeadingVisual>
                {option.name}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {duplicateLink}
      <RepositoryAndIssuePicker
        issuePickerProps={{insidePortal: true, responsiveOnNarrow: true}}
        repositoryPickerProps={{responsiveOnNarrow: true, insidePortal: true, hideContainer: true}}
        selectedIssueIds={[]}
        hiddenIssueIds={[issueId]}
        onPickerTypeChange={type => {
          if (type === null && duplicateStatus) {
            reportIssueStatusesUsage({
              surface: isSticky ? 'issue_header_sticky' : 'issue_header',
              cause: 'explicit_selection',
              action: 'select',
              phase: 'cancelled',
              requestComparison: savedStatus
                ? savedStatus.id === duplicateStatus.id
                  ? 'same'
                  : 'different'
                : 'unknown',
            })
          }
          setPickerType(type)
        }}
        onIssueSelection={issues => {
          setPickerType(null)
          if (issues[0] && duplicateStatus) submit(duplicateStatus, issues[0])
        }}
        defaultRepositoryNameWithOwner={repositoryNameWithOwner ?? `${owner}/${repo}`}
        organization={owner}
        pickerType={pickerType}
        anchorElement={props => {
          const {ref} = props as React.HTMLAttributes<HTMLButtonElement> & {
            ref: React.RefObject<HTMLButtonElement | null>
          }
          if (ref) ref.current = anchorRef.current
          // The status button above is the anchor, so the picker doesn't render its own.
          return <></>
        }}
      />
    </div>
  )
}
